/**
 * Hardened window.open for URLs that come from data (author-entered link
 * components, API-provided download/action URLs, notification targets).
 *
 * Audit M2: raw `window.open(url, '_blank')` calls had two problems:
 * - No scheme validation: a stored `javascript:` URL (written via another
 *   client or a compromised author account) executes in the viewer's
 *   session the moment they click "Open link" — stored XSS.
 * - No `noopener`: the opened page receives a `window.opener` handle to
 *   this app (reverse tabnabbing).
 *
 * Only http(s) URLs (and same-origin paths) are allowed. Anything else is
 * refused loudly — a silently-dead button would violate the fail-loudly
 * rule and mask data problems.
 */

export class BlockedExternalUrlError extends Error {
  readonly url: string;

  constructor(url: string) {
    super(
      'This link could not be opened because its address is not a valid ' +
        'http(s) URL.',
    );
    this.name = 'BlockedExternalUrlError';
    this.url = url;
  }
}

/** True when the URL parses as http(s), or is a relative same-origin path. */
export function isSafeExternalUrl(url: string | null | undefined): url is string {
  if (!url) return false;
  let parsed: URL;
  try {
    // Relative paths resolve against the current origin and come out http(s).
    parsed = new URL(url, window.location.origin);
  } catch {
    return false;
  }
  return parsed.protocol === 'http:' || parsed.protocol === 'https:';
}

/**
 * Open a data-derived URL in a new tab with opener isolation.
 * Throws {@link BlockedExternalUrlError} for non-http(s) schemes
 * (javascript:, data:, vbscript:, file:, ...) — callers surface the error
 * to the user via their feedback mechanism.
 */
export function openExternal(url: string | null | undefined): void {
  if (!isSafeExternalUrl(url)) {
    throw new BlockedExternalUrlError(String(url));
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}
